import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import AppModal from "../AppModal";
import AuthField from "../Auth/AuthField";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const DEFAULT_COLOR = "#3b82f6";

const TagSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, "Mensagem muito curta")
		.required("Obrigatório"),
});

const initialState = {
	name: "",
	color: DEFAULT_COLOR,
	tagAusente: false,
};

function toPickerColor(color) {
	if (!color) return DEFAULT_COLOR;
	const hex = color.startsWith("#") ? color.slice(1) : color;
	return `#${hex.slice(0, 6)}`;
}

function TagModal({ open, onClose, tagId, reload }) {
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const [tag, setTag] = useState(initialState);
	const [loading, setLoading] = useState(false);

	useEffect(
		() => () => {
			isMounted.current = false;
		},
		[]
	);

	useEffect(() => {
		if (!open) return undefined;

		const fetchTag = async () => {
			if (!tagId) {
				setTag(initialState);
				return;
			}

			setLoading(true);
			try {
				const { data } = await api.get(`/tags/${tagId}`);
				if (isMounted.current) {
					setTag({
						name: data.name || "",
						color: data.color || DEFAULT_COLOR,
						tagAusente: Boolean(data.tagAusente),
					});
				}
			} catch (err) {
				toastError(err);
			} finally {
				if (isMounted.current) setLoading(false);
			}
		};

		fetchTag();
	}, [tagId, open]);

	const handleClose = () => {
		setTag(initialState);
		onClose();
	};

	const handleSaveTag = async (values) => {
		const tagData = { ...values, userId: user.id };
		try {
			if (tagId) {
				await api.put(`/tags/${tagId}`, tagData);
			} else {
				await api.post("/tags", tagData);
			}
			toast.success(i18n.t("tagModal.success"));
			if (typeof reload === "function") {
				reload();
			}
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	const title = tagId ? i18n.t("tagModal.title.edit") : i18n.t("tagModal.title.add");

	return (
		<Formik
			initialValues={tag}
			enableReinitialize
			validationSchema={TagSchema}
			onSubmit={(values, actions) => {
				handleSaveTag(values);
				actions.setSubmitting(false);
			}}
		>
			{({
				values,
				errors,
				touched,
				isSubmitting,
				handleChange,
				handleBlur,
				setFieldValue,
			}) => (
				<AppModal
					open={open}
					onClose={handleClose}
					title={title}
					maxWidth={480}
					footer={
						<>
							<button
								type="button"
								className="btn btn-sub-gray"
								onClick={handleClose}
								disabled={isSubmitting}
							>
								{i18n.t("tagModal.buttons.cancel")}
							</button>
							<button
								type="submit"
								form="tag-form"
								className="btn btn-primary"
								disabled={isSubmitting || loading}
							>
								{tagId
									? i18n.t("tagModal.buttons.okEdit")
									: i18n.t("tagModal.buttons.okAdd")}
							</button>
						</>
					}
				>
					{loading ? (
						<div className="app-modal-loading">
							<div className="app-modal-spinner" />
						</div>
					) : (
						<Form id="tag-form" className="space-y-4">
							<AuthField
								id="tag-name"
								label={i18n.t("tagModal.form.name")}
								error={touched.name && errors.name}
							>
								<input
									id="tag-name"
									name="name"
									type="text"
									className="form-input w-full"
									value={values.name}
									onChange={handleChange}
									onBlur={handleBlur}
									autoFocus
								/>
							</AuthField>

							<AuthField
								id="tag-color"
								label={i18n.t("tagModal.form.color")}
								error={touched.color && errors.color}
							>
								<div className="flex items-center gap-2">
									<span
										className="tag-modal-color-swatch shrink-0"
										style={{ backgroundColor: values.color || DEFAULT_COLOR }}
										aria-hidden
									/>
									<input
										id="tag-color"
										name="color"
										type="text"
										className="form-input min-w-0 flex-1"
										placeholder="#3b82f6"
										value={values.color}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									<input
										type="color"
										className="tag-modal-color-input shrink-0"
										value={toPickerColor(values.color)}
										onChange={(e) => setFieldValue("color", e.target.value)}
										aria-label={i18n.t("tagModal.form.pickColor")}
									/>
								</div>
							</AuthField>

							<label className="flex cursor-pointer items-center gap-2.5">
								<input
									type="checkbox"
									name="tagAusente"
									className="form-checkbox"
									checked={Boolean(values.tagAusente)}
									onChange={handleChange}
								/>
								<span className="text-sm text-default-700 dark:text-default-200">
									{i18n.t("tagModal.form.absentTag")}
								</span>
							</label>
						</Form>
					)}
				</AppModal>
			)}
		</Formik>
	);
}

export default TagModal;
